import { useState } from 'react';
import { supabase } from '../lib/supabase';

interface DriverFullProfile {
  race_number: string;
  phone?: string;
  address?: string;
  country?: string;
  age?: number;
  team_name?: string;
  car_model?: string;
  car_make?: string;
  vehicle_model?: string;
  year_of_model?: number;
  club_name?: string;
  nationality?: string;
  first_name?: string;
  last_name?: string;
  nickname?: string;
  search_name?: string;
  date_of_birth?: string;
  id_number?: string;
}

export const useDriverProfileAutofill = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchDriverFullProfileByName = async (searchName: string): Promise<DriverFullProfile | null> => {
    try {
      setLoading(true);
      setError(null);

      console.log(`Searching for driver with name: "${searchName}"`);
      
      // Clean the search term
      const cleanedName = searchName.trim();
      console.log(`Cleaned search term: "${cleanedName}"`);
      
      if (!cleanedName) {
        console.log('Empty search term, returning null');
        return null;
      }

      // Use the database function for unified search
      const { data: functionData, error: functionError } = await supabase
        .rpc('search_driver_by_name', { search_term: cleanedName });
        
      if (functionError) {
        console.error('Function search error:', functionError);
        throw functionError;
      } else {
        console.log('Function search results:', functionData);
        
        if (functionData && functionData.length > 0) {
          return functionData[0];
        }
      }

      // Direct search on search_name column as fallback
      const { data: searchNameData, error: searchNameError } = await supabase
        .from('driver_full_profile')
        .select('*')
        .ilike('search_name', `%${cleanedName}%`)
        .limit(1);
        
      if (searchNameError) {
        console.error('Search name error:', searchNameError);
      } else {
        console.log('Search name results:', searchNameData);
        
        if (searchNameData && searchNameData.length > 0) {
          return searchNameData[0];
        }
      }
      
      console.log('No driver profile found with the given search criteria');
      return null;
    } catch (err) {
      console.error('Error fetching driver full profile by name:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch driver profile');
      return null;
    } finally {
      setLoading(false);
    }
  };

  const fetchDriverFullProfileByRaceNumber = async (raceNumber: string): Promise<DriverFullProfile | null> => {
    try {
      setLoading(true);
      setError(null);

      console.log(`Searching for driver with race number: "${raceNumber}"`);
      
      const { data, error } = await supabase
        .from('driver_full_profile')
        .select('*')
        .eq('race_number', raceNumber)
        .limit(1);

      if (error) {
        console.error('Race number search error:', error);
        throw error;
      }
      
      console.log('Race number search results:', data);
      
      if (data && data.length > 0) {
        return data[0];
      }
      
      return null;
    } catch (err) {
      console.error('Error fetching driver full profile by race number:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch driver profile');
      return null;
    } finally {
      setLoading(false);
    }
  };

  const checkRaceNumberExists = async (raceNumber: string): Promise<{exists: boolean, driverName?: string}> => {
    try {
      setLoading(true);
      setError(null);

      if (!raceNumber || raceNumber.trim() === '') {
        return { exists: false };
      }

      const { data, error } = await supabase
        .rpc('check_race_number_exists', { p_race_number: raceNumber });

      if (error) {
        console.error('Race number check error:', error);
        throw error;
      }
      
      if (data && data.length > 0) {
        return { 
          exists: data[0].number_exists, 
          driverName: data[0].driver_name
        };
      }
      
      return { exists: false };
    } catch (err) {
      console.error('Error checking race number:', err);
      setError(err instanceof Error ? err.message : 'Failed to check race number');
      return { exists: false };
    } finally {
      setLoading(false);
    }
  };

  const populateDriverProfile = async (
    searchValue: string,
    vehicleType: 'auto' | 'motorcycle',
    setPersonalInfo: (info: any) => void,
    setVehicleInfo: (info: any) => void,
    setDriverInfo: (info: any) => void,
    isRaceNumber: boolean = true
  ) => {
    try {
      setLoading(true);
      setError(null);

      let profile: DriverFullProfile | null = null;
      
      if (isRaceNumber) {
        // Search by race number
        profile = await fetchDriverFullProfileByRaceNumber(searchValue);
      } else {
        // Search by name
        profile = await fetchDriverFullProfileByName(searchValue);
      }
      
      if (!profile) {
        setError(isRaceNumber 
          ? 'No existing profile found with this race number' 
          : 'No existing profile found with this name');
        return false;
      }

      console.log('Found profile:', profile);

      // Populate driver info
      setDriverInfo(prevInfo => ({
        ...prevInfo,
        driverName: `${profile?.first_name || ''} ${profile?.last_name || ''}`.trim(),
        nickname: profile?.nickname || prevInfo.nickname,
        teamName: profile?.team_name || prevInfo.teamName,
        clubName: profile?.club_name || prevInfo.clubName,
        address: profile?.address || prevInfo.address,
        dateOfBirth: profile?.date_of_birth || prevInfo.dateOfBirth,
        nationality: profile?.nationality || prevInfo.nationality,
        country: profile?.country || prevInfo.country,
        mobilePhone: profile?.phone ? String(profile.phone).replace('.0', '') : prevInfo.mobilePhone,
        idNumber: profile?.id_number || prevInfo.idNumber
      }));

      // Populate vehicle info with combined field
      setVehicleInfo(prevInfo => {
        // If vehicle_model exists, use it for the combined field
        if (profile?.vehicle_model) {
          return {
            ...prevInfo,
            raceNumber: profile.race_number || prevInfo.raceNumber,
            vehicleBrandModel: profile.vehicle_model, // Use the combined field
            year: profile?.year_of_model?.toString() || prevInfo.year
          };
        } else {
          // Otherwise use separate fields
          return {
            ...prevInfo,
            raceNumber: profile.race_number || prevInfo.raceNumber,
            vehicleBrandModel: `${profile?.car_make || ''} ${profile?.car_model || ''}`.trim(),
            year: profile?.year_of_model?.toString() || prevInfo.year
          };
        }
      });

      // Populate personal info (vehicle owner info)
      setPersonalInfo(prevInfo => ({
        ...prevInfo,
        fullName: `${profile?.first_name || ''} ${profile?.last_name || ''}`.trim(),
        address: profile?.address || prevInfo.address,
        mobilePhone: profile?.phone ? String(profile.phone).replace('.0', '') : prevInfo.mobilePhone,
        nationality: profile?.nationality || prevInfo.nationality,
        country: profile?.country || prevInfo.country,
        idNumber: profile?.id_number || prevInfo.idNumber,
        dateOfBirth: profile?.date_of_birth || prevInfo.dateOfBirth
      }));

      return true;
    } catch (err) {
      console.error('Error populating driver profile:', err);
      setError(err instanceof Error ? err.message : 'Failed to populate driver profile');
      return false;
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    error,
    fetchDriverFullProfileByName,
    fetchDriverFullProfileByRaceNumber,
    checkRaceNumberExists,
    populateDriverProfile
  };
};